import { HttpException, viewerHttp } from '../http';

export enum DiagnosisStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  NOT_FOUND = 'NOT_FOUND',
}

export interface FindingAssessment {
  findingId?: string | null;
  name?: string;
  isClassificationCorrect?: boolean;
  isDetectionCorrect?: boolean;
}

export interface AssessmentSubmission {
  findingId: string | null;
  name?: string;
  isClassificationCorrect?: boolean;
  isDetectionCorrect?: boolean;
}

interface DiagnosisStatusResponse {
  status?: DiagnosisStatus;
}

interface ReportAssessmentsResponse {
  reportId?: string;
  findingAssessments?: FindingAssessment[];
}

const studyPath = (studyInstanceUid: string) => `studies/${encodeURIComponent(studyInstanceUid)}`;

export async function fetchDiagnosisStatus(studyInstanceUid: string): Promise<DiagnosisStatus> {
  const { data } = await viewerHttp.get<DiagnosisStatusResponse>('diagnoses/status', {
    params: { studyInstanceUid },
  });

  return data?.status ?? DiagnosisStatus.NOT_FOUND;
}

export async function triggerDiagnosis(studyInstanceUid: string): Promise<void> {
  await viewerHttp.post('diagnoses/trigger', { studyInstanceUid });
}

export async function revokeReport(studyInstanceUid: string): Promise<void> {
  await viewerHttp.post(`${studyPath(studyInstanceUid)}/reports/revoke`, { studyInstanceUid });
}

export async function fetchReportAssessments(
  studyInstanceUid: string
): Promise<{ reportId?: string; findingAssessments: FindingAssessment[] }> {
  const { data } = await viewerHttp.get<ReportAssessmentsResponse>(
    `${studyPath(studyInstanceUid)}/reports/assessments`
  );

  return { reportId: data?.reportId, findingAssessments: data?.findingAssessments ?? [] };
}

export async function submitReportAssessments(
  studyInstanceUid: string,
  assessments: AssessmentSubmission[]
): Promise<void> {
  await viewerHttp.post(`${studyPath(studyInstanceUid)}/reports/assess`, { assessments });
}

export enum OrganEnum {
  PANCREAS = 'PANCREAS',
  KIDNEY = 'KIDNEY',
  LIVER = 'LIVER',
}

export enum OrganQualityEnum {
  GOOD = 'GOOD',
  POOR = 'POOR',
}

export enum OrganReviewStatusEnum {
  IN_PROGRESS = 'IN_PROGRESS',
  NOT_FOUND = 'NOT_FOUND',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export const ORGANS: readonly OrganEnum[] = Object.values(OrganEnum);

export const ORGAN_DISPLAY_NAMES: Record<OrganEnum, string> = {
  [OrganEnum.PANCREAS]: 'Pancreas',
  [OrganEnum.KIDNEY]: 'Kidney',
  [OrganEnum.LIVER]: 'Liver',
};

export const ORGAN_QA_LABELS: Record<OrganEnum, string> = {
  [OrganEnum.PANCREAS]: 'QAPancreas',
  [OrganEnum.KIDNEY]: 'QAKidney',
  [OrganEnum.LIVER]: 'QALiver',
};

export interface OrganReview {
  reviewId: string;
  organ: OrganEnum;
  status?: OrganReviewStatusEnum;
  quality?: OrganQualityEnum;
}

export interface OrganReviewSubmission {
  reviewId: string;
  quality: OrganQualityEnum;
}

const ORGAN_VALUES = new Set<string>(ORGANS);
const QUALITY_VALUES = new Set<string>(Object.values(OrganQualityEnum));
const STATUS_VALUES = new Set<string>(Object.values(OrganReviewStatusEnum));

function toOrganReview(row: unknown): OrganReview | null {
  if (!row || typeof row !== 'object') {
    return null;
  }

  const { reviewId, organ, status, quality } = row as Record<string, unknown>;

  if (typeof reviewId !== 'string' || !reviewId) {
    return null;
  }

  if (typeof organ !== 'string' || !ORGAN_VALUES.has(organ)) {
    return null;
  }

  return {
    reviewId,
    organ: organ as OrganEnum,
    status:
      typeof status === 'string' && STATUS_VALUES.has(status)
        ? (status as OrganReviewStatusEnum)
        : undefined,
    quality:
      typeof quality === 'string' && QUALITY_VALUES.has(quality)
        ? (quality as OrganQualityEnum)
        : undefined,
  };
}

export async function fetchOrganReviews(seriesInstanceUid: string): Promise<OrganReview[]> {
  const { data, status } = await viewerHttp.get<unknown>('organ-reviews', {
    params: { seriesInstanceUID: seriesInstanceUid },
  });

  if (!Array.isArray(data)) {
    throw new HttpException(status, `organ-reviews answered ${status} with a body that is not an array`);
  }

  const reviews = data.map(toOrganReview).filter((review): review is OrganReview => review !== null);

  if (reviews.length !== data.length) {
    console.error(
      `[sycai] organ-reviews returned ${data.length - reviews.length} unusable row(s); ` +
        `they are treated as not yet issued`
    );
  }

  return reviews;
}

export async function submitOrganReviews(
  seriesInstanceUid: string,
  reviews: OrganReviewSubmission[]
): Promise<void> {
  await viewerHttp.post('organ-reviews', { seriesInstanceUID: seriesInstanceUid, reviews });
}

export interface UploadOptions {
  force?: boolean;
  signal?: AbortSignal;
  onProgress?: (loaded: number, total: number) => void;
}

interface UploadResponse {
  id?: { studyInstanceUID?: string };
}

const UPLOAD_TIMEOUT_MS = 60000 * 5;

export async function uploadDicomFile(file: File, options: UploadOptions = {}): Promise<string> {
  const body = new FormData();
  body.append('file', file);

  const { data, status } = await viewerHttp.post<UploadResponse>('dicom-web/studies', body, {
    timeout: UPLOAD_TIMEOUT_MS,
    signal: options.signal,
    onUploadProgress: options.onProgress,

    headers: options.force ? { 'x-skip-validation': 'true' } : undefined,
  });

  const studyInstanceUID = data?.id?.studyInstanceUID;

  if (typeof studyInstanceUID !== 'string' || !studyInstanceUID) {
    throw new HttpException(status, `upload answered ${status} with no id.studyInstanceUID`);
  }

  return studyInstanceUID;
}

export async function deleteStudy(studyInstanceUid: string): Promise<void> {
  await viewerHttp.delete(`dicom-web/studies/${encodeURIComponent(studyInstanceUid)}`);
}
